/**
 * displayName — render-time title-casing for SHOUTY source names.
 *
 * US federal (RIDB) campground names arrive ALL-CAPS ("FISHING CREEK
 * CAMPGROUND"). This helper title-cases a name at RENDER time only when it is
 * essentially all uppercase (≥85% of letters), so normal mixed-case names —
 * i.e. every AU name — pass through byte-identical. The stored name is never
 * mutated; slugs/searches keep using the raw value.
 */

const SMALL_WORDS = new Set([
  'a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'in', 'nor',
  'of', 'on', 'or', 'so', 'the', 'to', 'up', 'via', 'yet',
]);

/**
 * Roman numerals stay uppercase ("LOOP II", "PHASE IV"). Restricted to I/V/X
 * combinations ≤4 chars so real words made of roman letters (MILL, CIVIL, DIX)
 * still get title-cased.
 */
const KEEP_UPPER = /^[IVX]{1,4}$/;

function capitalise(word: string): string {
  // Capitalise the first LETTER, preserving leading digits/punctuation
  // ("4WD" stays, "(NORTH" → "(North").
  const i = word.search(/[a-zA-Z]/);
  if (i === -1) return word;
  return word.slice(0, i) + word[i].toUpperCase() + word.slice(i + 1);
}

export function displayName(name?: string | null): string {
  if (!name) return '';

  const letters = name.replace(/[^A-Za-z]/g, '');
  if (letters.length < 4) return name; // too short to judge (acronyms, codes)
  if (!/\s/.test(name.trim())) return name; // single word → likely an acronym (RACV)
  const upper = letters.replace(/[^A-Z]/g, '').length;
  if (upper / letters.length < 0.85) return name; // mixed case → untouched

  const words = name.split(/(\s+)/); // keep whitespace runs intact
  const lastWordIndex = (() => {
    for (let i = words.length - 1; i >= 0; i--) {
      if (words[i].trim()) return i;
    }
    return -1;
  })();

  let firstSeen = false;
  return words
    .map((token, i) => {
      if (!token.trim()) return token;
      if (KEEP_UPPER.test(token)) return token; // roman numerals: LOOP II
      // Hyphenated/slashed parts each get cased ("BLUE-GRAY" → "Blue-Gray").
      const cased = token
        .toLowerCase()
        .split(/([-/])/)
        .map((part) => (part === '-' || part === '/' ? part : capitalise(part)))
        .join('');
      const isFirst = !firstSeen;
      firstSeen = true;
      const isLast = i === lastWordIndex;
      if (!isFirst && !isLast && SMALL_WORDS.has(token.toLowerCase())) {
        return token.toLowerCase();
      }
      return cased;
    })
    .join('');
}
